import { ToolItem } from '../types';

export interface ImageToolItem extends ToolItem {
  imageCategory: 'compress-resize' | 'crop-transform' | 'convert' | 'svg' | 'effects-filters' | 'colors-background' | 'metadata-exif' | 'generators-watermark';
  imageCategoryLabel: string;
  iconBgColor: string;
  iconTextColor: string;
  iconName: string;
}

export const IMAGE_CATEGORIES_TABS = [
  { id: 'all', label: 'All Image Tools', icon: 'Grid' },
  { id: 'compress-resize', label: 'Compress & Resize', icon: 'Archive' },
  { id: 'crop-transform', label: 'Crop & Transform', icon: 'Crop' },
  { id: 'convert', label: 'Format Converter', icon: 'RefreshCw' },
  { id: 'svg', label: 'SVG Tools', icon: 'Code' },
  { id: 'effects-filters', label: 'Effects & Filters', icon: 'Sliders' },
  { id: 'colors-background', label: 'Color & Background', icon: 'Palette' },
  { id: 'metadata-exif', label: 'Metadata & EXIF', icon: 'Info' },
  { id: 'generators-watermark', label: 'Generators & Watermark', icon: 'Stamp' }
];

export const IMAGE_TOOLS_DATA: ImageToolItem[] = [
  // --- COMPRESS & RESIZE (7 Tools) ---
  {
    id: 'image-compressor',
    name: 'Image Compressor',
    description: 'Compress PNG, JPG, and WebP images to smaller file sizes without losing quality.',
    imageCategory: 'compress-resize',
    imageCategoryLabel: 'Compress & Resize',
    iconBgColor: 'bg-blue-500/15',
    iconTextColor: 'text-blue-500',
    iconName: 'Archive',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'image-resizer',
    name: 'Image Resizer',
    description: 'Resize image dimensions in pixels, percentages, or exact width and height ratios.',
    imageCategory: 'compress-resize',
    imageCategoryLabel: 'Compress & Resize',
    iconBgColor: 'bg-indigo-500/15',
    iconTextColor: 'text-indigo-500',
    iconName: 'Maximize2',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'batch-resize',
    name: 'Batch Resize',
    description: 'Resize dozens of image files simultaneously with unified custom dimension rules.',
    imageCategory: 'compress-resize',
    imageCategoryLabel: 'Compress & Resize',
    iconBgColor: 'bg-purple-500/15',
    iconTextColor: 'text-purple-500',
    iconName: 'Copy',
    actionType: 'generic'
  },
  {
    id: 'batch-compress',
    name: 'Batch Compress',
    description: 'Compress multiple images in bulk to save bandwidth and storage space.',
    imageCategory: 'compress-resize',
    imageCategoryLabel: 'Compress & Resize',
    iconBgColor: 'bg-emerald-500/15',
    iconTextColor: 'text-emerald-500',
    iconName: 'Minimize2',
    actionType: 'generic'
  },
  {
    id: 'image-size-checker',
    name: 'Image Size Checker',
    description: 'Check image resolution, raw byte size, compression ratio, and storage metrics.',
    imageCategory: 'compress-resize',
    imageCategoryLabel: 'Compress & Resize',
    iconBgColor: 'bg-amber-500/15',
    iconTextColor: 'text-amber-500',
    iconName: 'Calculator',
    actionType: 'generic'
  },
  {
    id: 'aspect-ratio-calculator',
    name: 'Aspect Ratio Calculator',
    description: 'Calculate standard aspect ratios (16:9, 4:3, 1:1, 9:16) for custom graphics.',
    imageCategory: 'compress-resize',
    imageCategoryLabel: 'Compress & Resize',
    iconBgColor: 'bg-cyan-500/15',
    iconTextColor: 'text-cyan-500',
    iconName: 'Percent',
    actionType: 'generic'
  },
  {
    id: 'image-dpi-viewer',
    name: 'Image DPI Viewer',
    description: 'Inspect image Dots Per Inch (DPI/PPI) resolution settings for print readiness.',
    imageCategory: 'compress-resize',
    imageCategoryLabel: 'Compress & Resize',
    iconBgColor: 'bg-sky-500/15',
    iconTextColor: 'text-sky-500',
    iconName: 'Eye',
    actionType: 'generic'
  },

  // --- CROP & TRANSFORM (9 Tools) ---
  {
    id: 'crop-image',
    name: 'Crop Image',
    description: 'Trim unwanted outer borders or crop graphics to custom rectangular ratios.',
    imageCategory: 'crop-transform',
    imageCategoryLabel: 'Crop & Transform',
    iconBgColor: 'bg-green-500/15',
    iconTextColor: 'text-green-500',
    iconName: 'Crop',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'rotate-image',
    name: 'Rotate Image',
    description: 'Rotate images clockwise or counter-clockwise by 90°, 180°, or custom angles.',
    imageCategory: 'crop-transform',
    imageCategoryLabel: 'Crop & Transform',
    iconBgColor: 'bg-teal-500/15',
    iconTextColor: 'text-teal-500',
    iconName: 'RotateCw',
    actionType: 'generic'
  },
  {
    id: 'flip-image',
    name: 'Flip Image',
    description: 'Flip images horizontally or vertically to mirror graphics instantly.',
    imageCategory: 'crop-transform',
    imageCategoryLabel: 'Crop & Transform',
    iconBgColor: 'bg-cyan-500/15',
    iconTextColor: 'text-cyan-500',
    iconName: 'FlipHorizontal',
    actionType: 'generic'
  },
  {
    id: 'image-mirror',
    name: 'Image Mirror',
    description: 'Create symmetrical mirror reflections of photos along vertical or horizontal axes.',
    imageCategory: 'crop-transform',
    imageCategoryLabel: 'Crop & Transform',
    iconBgColor: 'bg-blue-500/15',
    iconTextColor: 'text-blue-500',
    iconName: 'Columns',
    actionType: 'generic'
  },
  {
    id: 'image-splitter',
    name: 'Image Splitter',
    description: 'Split large images into grid tiles, Instagram carousels, or equal slices.',
    imageCategory: 'crop-transform',
    imageCategoryLabel: 'Crop & Transform',
    iconBgColor: 'bg-orange-500/15',
    iconTextColor: 'text-orange-500',
    iconName: 'Scissors',
    actionType: 'generic'
  },
  {
    id: 'image-joiner',
    name: 'Image Joiner',
    description: 'Combine or stitch multiple photos horizontally or vertically into one collage.',
    imageCategory: 'crop-transform',
    imageCategoryLabel: 'Crop & Transform',
    iconBgColor: 'bg-indigo-500/15',
    iconTextColor: 'text-indigo-500',
    iconName: 'LayoutGrid',
    actionType: 'generic'
  },
  {
    id: 'image-border-adder',
    name: 'Image Border Adder',
    description: 'Add decorative frame borders, padding colors, and stroke lines around images.',
    imageCategory: 'crop-transform',
    imageCategoryLabel: 'Crop & Transform',
    iconBgColor: 'bg-pink-500/15',
    iconTextColor: 'text-pink-500',
    iconName: 'Square',
    actionType: 'generic'
  },
  {
    id: 'rounded-corners',
    name: 'Rounded Corners',
    description: 'Round image corners smooth with customizable pixel radius and anti-aliasing.',
    imageCategory: 'crop-transform',
    imageCategoryLabel: 'Crop & Transform',
    iconBgColor: 'bg-fuchsia-500/15',
    iconTextColor: 'text-fuchsia-500',
    iconName: 'CircleDot',
    actionType: 'generic'
  },
  {
    id: 'circle-crop',
    name: 'Circle Crop',
    description: 'Crop photos into perfect circular shapes with transparent PNG backgrounds.',
    imageCategory: 'crop-transform',
    imageCategoryLabel: 'Crop & Transform',
    iconBgColor: 'bg-rose-500/15',
    iconTextColor: 'text-rose-500',
    iconName: 'Circle',
    actionType: 'generic'
  },

  // --- FORMAT CONVERTER (13 Tools) ---
  {
    id: 'convert-png-jpg',
    name: 'Convert PNG ↔ JPG',
    description: 'Convert PNG graphics to compressed JPG format or vice versa with full quality.',
    imageCategory: 'convert',
    imageCategoryLabel: 'Format Converter',
    iconBgColor: 'bg-teal-500/15',
    iconTextColor: 'text-teal-500',
    iconName: 'RefreshCw',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'convert-png-webp',
    name: 'Convert PNG ↔ WebP',
    description: 'Convert PNG files to next-gen lightweight WebP format for fast web rendering.',
    imageCategory: 'convert',
    imageCategoryLabel: 'Format Converter',
    iconBgColor: 'bg-emerald-500/15',
    iconTextColor: 'text-emerald-500',
    iconName: 'Zap',
    actionType: 'generic'
  },
  {
    id: 'convert-jpg-webp',
    name: 'Convert JPG ↔ WebP',
    description: 'Transform JPG photos into modern WebP images to optimize web speed.',
    imageCategory: 'convert',
    imageCategoryLabel: 'Format Converter',
    iconBgColor: 'bg-blue-500/15',
    iconTextColor: 'text-blue-500',
    iconName: 'RefreshCw',
    actionType: 'generic'
  },
  {
    id: 'convert-avif',
    name: 'Convert AVIF',
    description: 'Convert ultra-compressed AVIF images to PNG, JPG, or WebP files effortlessly.',
    imageCategory: 'convert',
    imageCategoryLabel: 'Format Converter',
    iconBgColor: 'bg-purple-500/15',
    iconTextColor: 'text-purple-500',
    iconName: 'FileImage',
    actionType: 'generic'
  },
  {
    id: 'convert-bmp',
    name: 'Convert BMP',
    description: 'Convert uncompressed Bitmap (BMP) graphics into web-friendly formats.',
    imageCategory: 'convert',
    imageCategoryLabel: 'Format Converter',
    iconBgColor: 'bg-indigo-500/15',
    iconTextColor: 'text-indigo-500',
    iconName: 'Image',
    actionType: 'generic'
  },
  {
    id: 'convert-gif',
    name: 'Convert GIF',
    description: 'Convert animated or static GIF files to PNG, JPG, or MP4 image formats.',
    imageCategory: 'convert',
    imageCategoryLabel: 'Format Converter',
    iconBgColor: 'bg-amber-500/15',
    iconTextColor: 'text-amber-500',
    iconName: 'Film',
    actionType: 'generic'
  },
  {
    id: 'convert-tiff',
    name: 'Convert TIFF',
    description: 'Convert high-resolution TIFF photography files into web standard formats.',
    imageCategory: 'convert',
    imageCategoryLabel: 'Format Converter',
    iconBgColor: 'bg-sky-500/15',
    iconTextColor: 'text-sky-500',
    iconName: 'FileText',
    actionType: 'generic'
  },
  {
    id: 'heic-converter',
    name: 'HEIC Converter',
    description: 'Convert Apple iOS HEIC and HEIF camera photos directly into JPG or PNG.',
    imageCategory: 'convert',
    imageCategoryLabel: 'Format Converter',
    iconBgColor: 'bg-rose-500/15',
    iconTextColor: 'text-rose-500',
    iconName: 'Smartphone',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'ico-generator',
    name: 'ICO Generator',
    description: 'Generate multi-resolution Favicon ICO files for website icons.',
    imageCategory: 'convert',
    imageCategoryLabel: 'Format Converter',
    iconBgColor: 'bg-fuchsia-500/15',
    iconTextColor: 'text-fuchsia-500',
    iconName: 'Globe',
    actionType: 'generic'
  },
  {
    id: 'batch-convert',
    name: 'Batch Convert',
    description: 'Convert multiple image files across different formats simultaneously in bulk.',
    imageCategory: 'convert',
    imageCategoryLabel: 'Format Converter',
    iconBgColor: 'bg-cyan-500/15',
    iconTextColor: 'text-cyan-500',
    iconName: 'Copy',
    actionType: 'generic'
  },
  {
    id: 'image-base64-encoder',
    name: 'Image Base64 Encoder',
    description: 'Encode image files into raw Base64 data strings or HTML img data URIs.',
    imageCategory: 'convert',
    imageCategoryLabel: 'Format Converter',
    iconBgColor: 'bg-violet-500/15',
    iconTextColor: 'text-violet-500',
    iconName: 'Code',
    actionType: 'generic'
  },
  {
    id: 'image-base64-decoder',
    name: 'Image Base64 Decoder',
    description: 'Decode Base64 strings or data URIs back into downloadable image files.',
    imageCategory: 'convert',
    imageCategoryLabel: 'Format Converter',
    iconBgColor: 'bg-blue-600/15',
    iconTextColor: 'text-blue-600',
    iconName: 'Binary',
    actionType: 'generic'
  },
  {
    id: 'qr-code-to-image',
    name: 'QR Code to Image',
    description: 'Generate customizable high-res QR code graphics for links and text.',
    imageCategory: 'convert',
    imageCategoryLabel: 'Format Converter',
    iconBgColor: 'bg-slate-500/15',
    iconTextColor: 'text-slate-400',
    iconName: 'QrCode',
    actionType: 'generic'
  },

  // --- SVG TOOLS (3 Tools) ---
  {
    id: 'svg-viewer',
    name: 'SVG Viewer',
    description: 'Inspect, view, zoom, and analyze vector SVG code structure online.',
    imageCategory: 'svg',
    imageCategoryLabel: 'SVG Tools',
    iconBgColor: 'bg-orange-500/15',
    iconTextColor: 'text-orange-500',
    iconName: 'Eye',
    actionType: 'generic'
  },
  {
    id: 'svg-optimizer',
    name: 'SVG Optimizer',
    description: 'Clean up vector paths, remove unused metadata, and optimize SVG files.',
    imageCategory: 'svg',
    imageCategoryLabel: 'SVG Tools',
    iconBgColor: 'bg-amber-500/15',
    iconTextColor: 'text-amber-500',
    iconName: 'Sliders',
    actionType: 'generic'
  },
  {
    id: 'svg-minifier',
    name: 'SVG Minifier',
    description: 'Minify SVG XML markup and reduce vector file sizes for production apps.',
    imageCategory: 'svg',
    imageCategoryLabel: 'SVG Tools',
    iconBgColor: 'bg-yellow-500/15',
    iconTextColor: 'text-yellow-600',
    iconName: 'Minimize',
    actionType: 'generic'
  },

  // --- EFFECTS & FILTERS (14 Tools) ---
  {
    id: 'blur-image',
    name: 'Blur Image',
    description: 'Apply Gaussian blur filters to soften focus or hide sensitive details.',
    imageCategory: 'effects-filters',
    imageCategoryLabel: 'Effects & Filters',
    iconBgColor: 'bg-blue-500/15',
    iconTextColor: 'text-blue-500',
    iconName: 'Droplet',
    actionType: 'generic'
  },
  {
    id: 'sharpen-image',
    name: 'Sharpen Image',
    description: 'Enhance image clarity and crispness by boosting edge detail contrast.',
    imageCategory: 'effects-filters',
    imageCategoryLabel: 'Effects & Filters',
    iconBgColor: 'bg-indigo-500/15',
    iconTextColor: 'text-indigo-500',
    iconName: 'Zap',
    actionType: 'generic'
  },
  {
    id: 'brightness-adjust',
    name: 'Brightness',
    description: 'Adjust image lightness and exposure levels with fine-tuned control.',
    imageCategory: 'effects-filters',
    imageCategoryLabel: 'Effects & Filters',
    iconBgColor: 'bg-amber-500/15',
    iconTextColor: 'text-amber-500',
    iconName: 'Sun',
    actionType: 'generic'
  },
  {
    id: 'contrast-adjust',
    name: 'Contrast',
    description: 'Modify tonal contrast balance between dark shadows and bright highlights.',
    imageCategory: 'effects-filters',
    imageCategoryLabel: 'Effects & Filters',
    iconBgColor: 'bg-slate-500/15',
    iconTextColor: 'text-slate-400',
    iconName: 'Contrast',
    actionType: 'generic'
  },
  {
    id: 'saturation-adjust',
    name: 'Saturation',
    description: 'Boost vibrant color intensity or tone down oversaturated photo shades.',
    imageCategory: 'effects-filters',
    imageCategoryLabel: 'Effects & Filters',
    iconBgColor: 'bg-rose-500/15',
    iconTextColor: 'text-rose-500',
    iconName: 'Palette',
    actionType: 'generic'
  },
  {
    id: 'hue-adjust',
    name: 'Hue',
    description: 'Shift color spectrum hues across 360 degrees to transform photo palettes.',
    imageCategory: 'effects-filters',
    imageCategoryLabel: 'Effects & Filters',
    iconBgColor: 'bg-purple-500/15',
    iconTextColor: 'text-purple-500',
    iconName: 'Sparkles',
    actionType: 'generic'
  },
  {
    id: 'grayscale-filter',
    name: 'Grayscale',
    description: 'Convert color photos into classic black and white monochrome tones.',
    imageCategory: 'effects-filters',
    imageCategoryLabel: 'Effects & Filters',
    iconBgColor: 'bg-zinc-500/15',
    iconTextColor: 'text-zinc-400',
    iconName: 'Moon',
    actionType: 'generic'
  },
  {
    id: 'sepia-filter',
    name: 'Sepia',
    description: 'Apply warm vintage sepia photo filters for a nostalgic antique look.',
    imageCategory: 'effects-filters',
    imageCategoryLabel: 'Effects & Filters',
    iconBgColor: 'bg-amber-700/15',
    iconTextColor: 'text-amber-700',
    iconName: 'Compass',
    actionType: 'generic'
  },
  {
    id: 'negative-filter',
    name: 'Negative',
    description: 'Invert image colors to generate film negative preview effects.',
    imageCategory: 'effects-filters',
    imageCategoryLabel: 'Effects & Filters',
    iconBgColor: 'bg-cyan-500/15',
    iconTextColor: 'text-cyan-500',
    iconName: 'EyeOff',
    actionType: 'generic'
  },
  {
    id: 'pixelate-filter',
    name: 'Pixelate',
    description: 'Pixelate photos with adjustable block sizes for retro 8-bit aesthetic.',
    imageCategory: 'effects-filters',
    imageCategoryLabel: 'Effects & Filters',
    iconBgColor: 'bg-teal-500/15',
    iconTextColor: 'text-teal-500',
    iconName: 'Grid',
    actionType: 'generic'
  },
  {
    id: 'sketch-effect',
    name: 'Sketch Effect',
    description: 'Transform photos into pencil line drawings or charcoal artistic sketches.',
    imageCategory: 'effects-filters',
    imageCategoryLabel: 'Effects & Filters',
    iconBgColor: 'bg-emerald-500/15',
    iconTextColor: 'text-emerald-500',
    iconName: 'PenTool',
    actionType: 'generic'
  },
  {
    id: 'cartoon-effect',
    name: 'Cartoon Effect',
    description: 'Apply comic book cel shading and posterized cartoon illustration effects.',
    imageCategory: 'effects-filters',
    imageCategoryLabel: 'Effects & Filters',
    iconBgColor: 'bg-pink-500/15',
    iconTextColor: 'text-pink-500',
    iconName: 'Smile',
    actionType: 'generic'
  },
  {
    id: 'vintage-filter',
    name: 'Vintage Filter',
    description: 'Add retro film grain, warm light leaks, and retro polaroid color grading.',
    imageCategory: 'effects-filters',
    imageCategoryLabel: 'Effects & Filters',
    iconBgColor: 'bg-orange-500/15',
    iconTextColor: 'text-orange-500',
    iconName: 'Camera',
    actionType: 'generic'
  },
  {
    id: 'noise-reduction',
    name: 'Noise Reduction',
    description: 'Smooth digital camera grain, ISO noise, and speckles from low-light photos.',
    imageCategory: 'effects-filters',
    imageCategoryLabel: 'Effects & Filters',
    iconBgColor: 'bg-blue-600/15',
    iconTextColor: 'text-blue-600',
    iconName: 'SlidersHorizontal',
    actionType: 'generic'
  },

  // --- COLOR & BACKGROUND (6 Tools) ---
  {
    id: 'background-color-changer',
    name: 'Background Color Changer',
    description: 'Replace photo background colors with custom solid hues, gradients, or patterns.',
    imageCategory: 'colors-background',
    imageCategoryLabel: 'Color & Background',
    iconBgColor: 'bg-emerald-500/15',
    iconTextColor: 'text-emerald-500',
    iconName: 'Palette',
    actionType: 'generic'
  },
  {
    id: 'transparent-background-editor',
    name: 'Transparent Background Editor',
    description: 'Isolate subjects and make image backgrounds transparent for PNG exports.',
    imageCategory: 'colors-background',
    imageCategoryLabel: 'Color & Background',
    iconBgColor: 'bg-teal-500/15',
    iconTextColor: 'text-teal-500',
    iconName: 'Eraser',
    actionType: 'generic'
  },
  {
    id: 'color-picker',
    name: 'Color Picker',
    description: 'Eyedropper tool to pick exact HEX, RGB, HSL, and CMYK color codes from images.',
    imageCategory: 'colors-background',
    imageCategoryLabel: 'Color & Background',
    iconBgColor: 'bg-indigo-500/15',
    iconTextColor: 'text-indigo-500',
    iconName: 'Pipette',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'palette-extractor',
    name: 'Palette Extractor',
    description: 'Extract beautiful color scheme palettes and hex codes directly from any photo.',
    imageCategory: 'colors-background',
    imageCategoryLabel: 'Color & Background',
    iconBgColor: 'bg-fuchsia-500/15',
    iconTextColor: 'text-fuchsia-500',
    iconName: 'LayoutGrid',
    actionType: 'generic'
  },
  {
    id: 'dominant-color-extractor',
    name: 'Dominant Color Extractor',
    description: 'Analyze primary dominant colors and secondary color weights in graphics.',
    imageCategory: 'colors-background',
    imageCategoryLabel: 'Color & Background',
    iconBgColor: 'bg-purple-500/15',
    iconTextColor: 'text-purple-500',
    iconName: 'PieChart',
    actionType: 'generic'
  },
  {
    id: 'image-histogram',
    name: 'Image Histogram',
    description: 'Visualize red, green, blue (RGB) and luminance channel histograms.',
    imageCategory: 'colors-background',
    imageCategoryLabel: 'Color & Background',
    iconBgColor: 'bg-sky-500/15',
    iconTextColor: 'text-sky-500',
    iconName: 'BarChart2',
    actionType: 'generic'
  },

  // --- METADATA & EXIF (4 Tools) ---
  {
    id: 'image-metadata-viewer',
    name: 'Image Metadata Viewer',
    description: 'Inspect image metadata attributes including dimension, color profile, and format.',
    imageCategory: 'metadata-exif',
    imageCategoryLabel: 'Metadata & EXIF',
    iconBgColor: 'bg-teal-500/15',
    iconTextColor: 'text-teal-500',
    iconName: 'Info',
    actionType: 'generic'
  },
  {
    id: 'exif-viewer',
    name: 'EXIF Viewer',
    description: 'Read camera model, shutter speed, ISO, focal length, and GPS location EXIF tags.',
    imageCategory: 'metadata-exif',
    imageCategoryLabel: 'Metadata & EXIF',
    iconBgColor: 'bg-blue-500/15',
    iconTextColor: 'text-blue-500',
    iconName: 'Camera',
    actionType: 'generic'
  },
  {
    id: 'exif-remover',
    name: 'EXIF Remover',
    description: 'Strip private camera metadata, location coordinates, and device tags before sharing.',
    imageCategory: 'metadata-exif',
    imageCategoryLabel: 'Metadata & EXIF',
    iconBgColor: 'bg-rose-500/15',
    iconTextColor: 'text-rose-500',
    iconName: 'Trash2',
    actionType: 'generic'
  },
  {
    id: 'image-difference-checker',
    name: 'Image Difference Checker',
    description: 'Compare two images side-by-side or overlaid to highlight visual differences.',
    imageCategory: 'metadata-exif',
    imageCategoryLabel: 'Metadata & EXIF',
    iconBgColor: 'bg-indigo-500/15',
    iconTextColor: 'text-indigo-500',
    iconName: 'Columns',
    actionType: 'generic'
  },

  // --- GENERATORS & WATERMARK (5 Tools) ---
  {
    id: 'watermark-image',
    name: 'Watermark Image',
    description: 'Add text or logo watermarks with opacity, positioning, and rotation controls.',
    imageCategory: 'generators-watermark',
    imageCategoryLabel: 'Generators & Watermark',
    iconBgColor: 'bg-purple-500/15',
    iconTextColor: 'text-purple-500',
    iconName: 'Stamp',
    isPopular: true,
    actionType: 'generic'
  },
  {
    id: 'meme-generator',
    name: 'Meme Generator',
    description: 'Create custom memes with impact typography, top/bottom text, and stickers.',
    imageCategory: 'generators-watermark',
    imageCategoryLabel: 'Generators & Watermark',
    iconBgColor: 'bg-amber-500/15',
    iconTextColor: 'text-amber-500',
    iconName: 'Smile',
    actionType: 'generic'
  },
  {
    id: 'thumbnail-generator',
    name: 'Thumbnail Generator',
    description: 'Generate web-ready thumbnail image sizes for social media and galleries.',
    imageCategory: 'generators-watermark',
    imageCategoryLabel: 'Generators & Watermark',
    iconBgColor: 'bg-blue-500/15',
    iconTextColor: 'text-blue-500',
    iconName: 'LayoutGrid',
    actionType: 'generic'
  },
  {
    id: 'avatar-generator',
    name: 'Avatar Generator',
    description: 'Generate customized profile avatars, circular badges, and user initials graphics.',
    imageCategory: 'generators-watermark',
    imageCategoryLabel: 'Generators & Watermark',
    iconBgColor: 'bg-emerald-500/15',
    iconTextColor: 'text-emerald-500',
    iconName: 'User',
    actionType: 'generic'
  },
  {
    id: 'favicon-generator',
    name: 'Favicon Generator',
    description: 'Create website favicons in standard 16x16, 32x32, and 180x180 Apple Touch sizes.',
    imageCategory: 'generators-watermark',
    imageCategoryLabel: 'Generators & Watermark',
    iconBgColor: 'bg-cyan-500/15',
    iconTextColor: 'text-cyan-500',
    iconName: 'Globe',
    actionType: 'generic'
  }
];
